/**
 * useRoleHome — 내 부서(claims role)의 홈 설정을 구독 (2026-11-02).
 *
 *   ① 숨길 위젯: 부서 문서 `home_widgets` — 권한 판정과 같은 사업부(auth.service roleUnitOf)의 부서 문서에서 읽는다.
 *      관리자(is_admin)는 그룹 설정을 건너뛰어 빈 집합.
 *   ② 기본 배치: 지금 보는 사업 홈(보드 = 법인_사업)의 부서 문서 `home_layout`. 중앙 사용자가 경원 홈을 볼 때처럼
 *      그 사업부에 내 부서 문서가 없으면 null(시스템 기본 배치).
 *   둘 다 같은 문서면 구독은 1개. rules: roles read = 활성 직원.
 */
import { useEffect, useMemo, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { db } from "@/services/firebase";
import { parseHomeLayout, type RoleUnit } from "@/services/roles.repository";
import { roleUnitOf } from "@/services/auth.service";
import { useAuth } from "@/hooks/useAuth";
import { hiddenWidgetKeys, type HomeWidgetToggles } from "@/domain/home_widgets";
import type { RoleHomeLayout } from "@/domain/user";

export interface RoleHome {
  /** 그룹 설정에서 끈 위젯 키(widgetKey). 관리자·부서 없음 → 빈 집합 */
  hidden: ReadonlySet<string>;
  /** 이 보드의 부서 기본 배치. 없으면 null */
  roleLayout: RoleHomeLayout | null;
  /** 두 문서의 첫 응답을 받았는지 */
  loaded: boolean;
}

type RoleDocState = { key: string; toggles: HomeWidgetToggles | null; layout: RoleHomeLayout | null; loaded: boolean };
const EMPTY: RoleDocState = { key: "", toggles: null, layout: null, loaded: false };

function useRoleDoc(unit: RoleUnit | null, role: string | null): RoleDocState {
  const key = unit && role ? `${unit.tenant}/${unit.biz}/${role}` : "";
  const [state, setState] = useState<RoleDocState>(EMPTY);
  useEffect(() => {
    if (!unit || !role) return;
    return onSnapshot(
      doc(db, "tenants", unit.tenant, "units", unit.biz, "roles", role),
      (snap) => {
        const d = snap.data();
        const toggles = d && d.home_widgets && typeof d.home_widgets === "object" ? (d.home_widgets as HomeWidgetToggles) : null;
        setState({ key, toggles, layout: parseHomeLayout(d?.home_layout), loaded: true });
      },
      () => setState({ ...EMPTY, key, loaded: true }),
    );
    // unit 객체는 호출마다 새로 만들어질 수 있어 key 문자열로 비교
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  // 부서 없음 → 바로 로딩 완료. 키가 바뀐 직후엔 옛 문서 값을 보이지 않는다
  if (!key) return { ...EMPTY, loaded: true };
  return state.key === key ? state : EMPTY;
}

export function useRoleHome(boardUnit: RoleUnit): RoleHome {
  const { user } = useAuth();
  const role = user?.role ?? null;
  const permUnit = useMemo(() => roleUnitOf(user?.tenants ?? []), [user?.tenants]);
  const sameUnit = permUnit.tenant === boardUnit.tenant && permUnit.biz === boardUnit.biz;

  const permDoc = useRoleDoc(permUnit, role);
  // 같은 문서면 두 번 구독하지 않는다
  const boardDoc = useRoleDoc(sameUnit ? null : boardUnit, role);

  return useMemo(() => {
    const layoutDoc = sameUnit ? permDoc : boardDoc;
    return {
      hidden: user?.isAdmin ? new Set<string>() : hiddenWidgetKeys(permDoc.toggles),
      roleLayout: layoutDoc.layout,
      loaded: permDoc.loaded && (sameUnit || boardDoc.loaded),
    };
  }, [user?.isAdmin, permDoc, boardDoc, sameUnit]);
}
